import { SparklesIcon } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';

import styles from './landing.module.css';

import clsxm from '@/lib/clsxm';
import { isPaidOnlyPreset } from '@/lib/presetAccess';

import { ArrowGlyph, tone } from '@/components/agents/primitives';
import PublicCompletion from '@/components/PublicCompletion';

import type { Preset } from '@/constants/presets';

import Collapsible from './Collapsible';
import {
  exampleInput,
  exampleOptions,
  type TemplateExample,
  useTemplateTranslation,
} from './kit';
import RichText from './RichText';
import type { TemplateSampleSet } from './sampleSets';
import TemplateSamples from './TemplateSamples';

import { CaptionItem } from '@/types/Caption';

// "Make it a song" under lyrics results. It renders nothing for other
// templates; loaded only once a run has finished.
const MakeItASong = dynamic(() => import('@/components/music/MakeItASong'), {
  ssr: false,
});

type Props = {
  preset: Preset;
  example?: TemplateExample | null;
  /** Visual templates show their finished samples instead of a text example. */
  samples?: TemplateSampleSet | null;
  captions?: CaptionItem[];
  /** Free mode: the visitor has used today's run for this template. */
  limitReached?: boolean;
  signupHref: string;
  onUpgradeClick?: () => void;
  /** What to tell a visitor before anything was generated. */
  emptyHint: string;
};

/**
 * The right-hand pane: a real example until the visitor generates, then
 * their results, newest first.
 */
export default function OutputPanel({
  preset,
  example,
  samples,
  captions = [],
  limitReached = false,
  signupHref,
  onUpgradeClick,
  emptyHint,
}: Props) {
  const { t } = useTemplateTranslation();
  const hasResults = captions.length > 0;
  const writing = captions.some((caption) => caption.pending);
  const latest = captions[0];
  const songLyrics =
    latest?.text && !latest.pending && !latest.error ? latest.text : null;

  return (
    <div
      aria-live='polite'
      aria-busy={writing}
      className='flex min-h-[20rem] flex-1 flex-col border-t border-[#0f0f28]/[0.07] bg-[#f9f9fc] p-5 sm:p-7 lg:border-s lg:border-t-0 lg:p-8 dark:border-white/[0.07] dark:bg-white/[0.015]'
    >
      {hasResults ? (
        <>
          <PaneHeader
            badge={
              writing ? (
                <Badge tone='live'>{t('Writing…')}</Badge>
              ) : (
                <Badge tone='done'>{t('Result')}</Badge>
              )
            }
          />
          <ul className='mt-5 flex-1 space-y-6 divide-y divide-[#0f0f28]/[0.07] dark:divide-white/[0.08]'>
            {captions.map((caption, index) => (
              <li
                key={caption.id}
                className={clsxm(styles.arrive, index > 0 && 'pt-6')}
              >
                <ul>
                  {caption.pending ? (
                    <Writing />
                  ) : caption.error ? (
                    <li className='rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20'>
                      {t('This output failed to generate. Please try again.')}
                    </li>
                  ) : (
                    <PublicCompletion
                      caption={caption}
                      onUpgradeClick={onUpgradeClick}
                      variant='panel'
                    />
                  )}
                </ul>
              </li>
            ))}
          </ul>
          {songLyrics ? (
            <MakeItASong
              slug={preset.slug}
              lyrics={songLyrics}
              className='mt-6'
            />
          ) : null}
          {limitReached && !writing ? (
            <KeepGoing
              preset={preset}
              signupHref={signupHref}
              onUpgradeClick={onUpgradeClick}
            />
          ) : null}
        </>
      ) : samples ? (
        <>
          <PaneHeader
            badge={<Badge tone='example'>{t('Examples')}</Badge>}
            meta={t(samples.heading)}
          />
          <div className='mt-5 flex-1'>
            <TemplateSamples set={samples} />
          </div>
          <p className={clsxm('mt-4 text-[13px]', tone.quiet)}>
            {t('Made with this tool. Click one to see it full size.')}
          </p>
        </>
      ) : example?.completion ? (
        <ExamplePreview preset={preset} example={example} />
      ) : (
        <Empty preset={preset} hint={emptyHint} />
      )}
    </div>
  );
}

function PaneHeader({
  badge,
  meta,
}: {
  badge: React.ReactNode;
  meta?: React.ReactNode;
}) {
  return (
    <div className='flex min-h-[1.75rem] items-center gap-3'>
      {badge}
      {meta ? (
        <p className={clsxm('min-w-0 truncate text-[13px]', tone.quiet)}>
          {meta}
        </p>
      ) : null}
    </div>
  );
}

function Badge({
  tone: badgeTone,
  children,
}: {
  tone: 'example' | 'live' | 'done';
  children: React.ReactNode;
}) {
  return (
    <span
      className={clsxm(
        'inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold',
        badgeTone === 'example' &&
          'bg-[#ececf6] text-[#4b4b66] dark:bg-white/[0.07] dark:text-[#c4c4da]',
        badgeTone === 'live' &&
          'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-200',
        badgeTone === 'done' &&
          'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
      )}
    >
      <span
        aria-hidden='true'
        className={clsxm(
          'h-1.5 w-1.5 rounded-full',
          badgeTone === 'example' && 'bg-[#9a9ab4]',
          badgeTone === 'live' && 'animate-pulse bg-primary-500',
          badgeTone === 'done' && 'bg-emerald-500',
        )}
      />
      {children}
    </span>
  );
}

function ExamplePreview({
  preset,
  example,
}: {
  preset: Preset;
  example: TemplateExample;
}) {
  const { t } = useTemplateTranslation();
  const input = exampleInput(example, preset.fields);
  const options = exampleOptions(example, preset.fields);
  const meta = [input, ...options].filter(Boolean).join(' · ');

  return (
    <>
      <PaneHeader
        badge={<Badge tone='example'>{t('Example')}</Badge>}
        meta={meta ? <span title={meta}>{meta}</span> : null}
      />
      <div className='mt-5 flex-1'>
        <Collapsible
          maxHeightClass='max-h-[22rem] lg:max-h-[24rem]'
          moreLabel={t('Show the full example')}
          footer={
            <p className={clsxm('text-[13px]', tone.quiet)}>
              {t(
                'A real result from this tool. Yours will be based on what you type.',
              )}
            </p>
          }
        >
          <RichText text={example.completion || ''} />
        </Collapsible>
      </div>
    </>
  );
}

function Writing() {
  const { t } = useTemplateTranslation();
  return (
    <li className='list-none' aria-label={t('Generating...')}>
      <div className='space-y-3' aria-hidden='true'>
        {['w-2/5', 'w-full', 'w-11/12', 'w-full', 'w-4/5', 'w-3/5'].map(
          (width, index) => (
            <div
              key={index}
              className={clsxm(
                'rounded-full',
                index === 0 ? 'h-4' : 'h-3',
                width,
                styles.shimmer,
              )}
            />
          ),
        )}
      </div>
    </li>
  );
}

function Empty({ preset, hint }: { preset: Preset; hint: string }) {
  const { t } = useTemplateTranslation();
  return (
    <div className='flex flex-1 flex-col items-center justify-center py-10 text-center'>
      <span
        className={clsxm(
          'flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ring-inset ring-[#0f0f28]/[0.06] dark:bg-white/[0.06] dark:text-primary-300 dark:ring-white/10',
          preset.iconBackground,
          preset.iconForeground,
        )}
      >
        <SparklesIcon className='h-6 w-6' aria-hidden='true' />
      </span>
      <p className={clsxm('mt-4 text-[15px] font-semibold', tone.ink)}>
        {t('Your results will appear here')}
      </p>
      <p className={clsxm('mt-1.5 max-w-xs text-[14px] leading-6', tone.body)}>
        {hint}
      </p>
    </div>
  );
}

function KeepGoing({
  preset,
  signupHref,
  onUpgradeClick,
}: {
  preset: Preset;
  signupHref: string;
  onUpgradeClick?: () => void;
}) {
  const { t } = useTemplateTranslation();
  const action =
    'inline-flex flex-none items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-[14px] font-semibold text-white transition-colors hover:bg-primary-700';
  return (
    <div className='mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white px-5 py-4 ring-1 ring-inset ring-[#0f0f28]/[0.07] dark:bg-white/[0.04] dark:ring-white/10'>
      <p className={clsxm('min-w-0 flex-1 text-[14px] leading-6', tone.body)}>
        <span className={clsxm('font-semibold', tone.ink)}>
          {t('Want more?')}
        </span>{' '}
        {isPaidOnlyPreset(preset)
          ? t(
              'Upgrade to keep generating with this tool and save every result.',
            )
          : t(
              'Create a free account to keep generating, save your results and use 300+ AI tools.',
            )}
      </p>
      {isPaidOnlyPreset(preset) && onUpgradeClick ? (
        <button type='button' onClick={onUpgradeClick} className={action}>
          {t('Upgrade')}
          <ArrowGlyph />
        </button>
      ) : (
        <a href={signupHref} className={action}>
          {t('Sign up free')}
          <ArrowGlyph />
        </a>
      )}
    </div>
  );
}
